import { CalendarClock } from 'lucide-react'
import {
  formatToolValue,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <CalendarClock className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function deadlineItems(result: unknown) {
  if (!result || typeof result !== 'object') return []
  const record = result as Record<string, unknown>
  const overdueTickets = Array.isArray(record.overdueTickets)
    ? record.overdueTickets
    : []
  const staleClaims = Array.isArray(record.staleClaims) ? record.staleClaims : []

  return [
    ...overdueTickets
      .filter((ticket): ticket is Record<string, unknown> => !!ticket && typeof ticket === 'object')
      .map((ticket) => ({
        id: pickString(ticket.id),
        title:
          pickString(ticket.findingKey, ticket.displayCode, ticket.controlRef) ??
          'Overdue ticket',
        subtitle: pickString(ticket.displayCode, ticket.controlRef),
        meta: [
          'overdue',
          ticket.daysSinceDetection
            ? `${formatToolValue(ticket.daysSinceDetection)} days`
            : null,
          formatToolValue(ticket.status),
        ],
        tone: 'high' as const,
      })),
    ...staleClaims
      .filter((claim): claim is Record<string, unknown> => !!claim && typeof claim === 'object')
      .map((claim) => ({
        id: pickString(claim.id),
        title:
          pickString(claim.displayCode, claim.controlRef, claim.claimType) ??
          'Stale claim',
        subtitle: pickString(claim.claimType),
        meta: ['stale', formatToolValue(claim.lastVerified)],
        tone: 'medium' as const,
      })),
  ]
}

export function SchedulingListToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const count =
    typeof state.result?.schedulesCreated === 'number'
      ? state.result.schedulesCreated
      : typeof state.result?.count === 'number'
        ? state.result.count
        : null
  const calendarDetail =
    typeof state.result?.calendarConfigured === 'boolean'
      ? state.result.calendarConfigured
        ? 'calendar configured'
        : 'calendar not configured'
      : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Listing schedules...'
      completedLabel={
        count !== null
          ? `${plural(count, 'schedule')} loaded`
          : 'Schedules loaded'
      }
      completedDetail={calendarDetail}
      completedIcon={completedIcon}
    />
  )
}

export function SchedulingGetDeadlinesToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const overdueCount =
    typeof state.result?.overdueTicketCount === 'number'
      ? state.result.overdueTicketCount
      : 0
  const staleCount =
    typeof state.result?.staleClaimCount === 'number'
      ? state.result.staleClaimCount
      : 0
  const openCount =
    typeof state.result?.openTicketCount === 'number'
      ? state.result.openTicketCount
      : 0
  const items = deadlineItems(state.result)
  const summary = [
    `${plural(openCount, 'open ticket')}`,
    overdueCount > 0 ? `${plural(overdueCount, 'overdue ticket')}` : null,
    staleCount > 0 ? `${plural(staleCount, 'stale claim')}` : null,
  ]
    .filter(Boolean)
    .join(', ')
  const completedDetail = [
    pickString(state.result?.auditCycleLabel),
    summary,
  ].filter(Boolean).join(' · ')

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Checking deadlines...'
        completedLabel='Deadlines loaded'
        loadingDetail={
          typeof state.args?.lookAheadDays === 'number'
            ? `${state.args.lookAheadDays} days`
            : null
        }
        completedDetail={completedDetail || null}
        completedIcon={completedIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No overdue tickets or stale claims'
          items={items.map((item) => ({
            ...item,
            tone: normalizeTone(item.tone),
          }))}
        />
      ) : null}
    </>
  )
}
